import { useEffect } from 'react'

import { F0AuraVoiceAnimation, F0Button } from '@factorialco/f0-react'
import {
  RoomAudioRenderer,
  SessionProvider,
  useRoomContext,
  useSession,
  useVoiceAssistant,
} from '@livekit/components-react'
import { ConnectionState, TokenSource } from 'livekit-client'

import i18n from 'modules/core/lib/i18n'

import { getCursor, removeCursor } from 'modules/ai_voice/lib/cursor'
import { registerRouteObserver, unregisterRouteObserver } from 'modules/ai_voice/lib/route-observer'
import { registerRpcMethods, unregisterRpcMethods } from 'modules/ai_voice/lib/rpc'
import { fetchToken } from 'modules/ai_voice/lib/token'

// TODO: Add real participant attributes, not hardcoded values
function createTokenSource(intent: string) {
  return TokenSource.literal(() =>
    fetchToken({
      participant_attributes: {
        intent,
        locale: i18n.locale,
        role: 'admin',
        name: 'Adria',
        company: 'Factorial',
      },
    })
  )
}

function RoomContext() {
  const room = useRoomContext()

  useEffect(() => {
    getCursor()
    registerRpcMethods(room)
    registerRouteObserver(room)
    return () => {
      removeCursor()
      unregisterRpcMethods(room)
      unregisterRouteObserver()
    }
  }, [room])

  return null
}

function VoiceAnimationConnected() {
  const { audioTrack, state } = useVoiceAssistant()

  return <F0AuraVoiceAnimation state={state} audioTrack={audioTrack} />
}

interface VoiceClientProps {
  intent: string
}

export function VoiceClient({ intent }: VoiceClientProps) {
  const tokenSource = createTokenSource(intent)
  const session = useSession(tokenSource)

  async function handleConnect() {
    await session.start()
  }

  async function handleDisconnect() {
    await session.end()
  }

  const isConnected = session.isConnected
  const isConnecting = session.connectionState === ConnectionState.Connecting

  return (
    <SessionProvider session={session}>
      {!isConnected && (
        <>
          <div className="factorial-voice-agent-container">
            <F0AuraVoiceAnimation state={isConnecting ? 'connecting' : 'disconnected'} />
          </div>
          <F0Button
            onClick={handleConnect}
            disabled={isConnecting}
            label={isConnecting ? 'Connecting...' : 'Start'}
            variant="default"
          />
        </>
      )}

      {isConnected && (
        <>
          <RoomContext />
          <RoomAudioRenderer />
          <div className="factorial-voice-agent-container">
            <VoiceAnimationConnected />
          </div>
          <F0Button onClick={handleDisconnect} label="Stop" variant="neutral" />
        </>
      )}
    </SessionProvider>
  )
}
